"use client";
import { Badge, Button, UncontrolledTooltip } from "reactstrap";

const ServiceOrderTableItem = ({
  serviceOrder,
  handleShow,
  handleDelete,
  handleUpdateStatus,
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "warning", text: "Pending" },
      accepted: { color: "info", text: "Accepted" },
      in_progress: { color: "primary", text: "In Progress" },
      completed: { color: "success", text: "Completed" },
      cancelled: { color: "danger", text: "Cancelled" },
      refunded: { color: "secondary", text: "Refunded" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      text: "Unknown",
    };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus !== serviceOrder.status) {
      if (window.confirm(`Change service order status to ${newStatus}?`)) {
        handleUpdateStatus(serviceOrder.id, newStatus);
      }
    }
  };

  return (
    <tr>
      <td className="order_id">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0">
            <span className="fw-semibold">
              #{serviceOrder.order_number || serviceOrder.id}
            </span>
          </div>
        </div>
      </td>

      <td className="customer">
        <div>
          <h6 className="mb-1">
            {serviceOrder.user?.name || "Unknown Customer"}
          </h6>
          <p className="text-muted mb-0 small">{serviceOrder.user?.email}</p>
          <p className="text-muted mb-0 small">
            {serviceOrder.user?.account_type_id === 1
              ? "Influencer"
              : serviceOrder.user?.account_type_id === 2
              ? "Business"
              : "User"}
          </p>
        </div>
      </td>

      <td className="service">
        <div className="d-flex align-items-center">
          {serviceOrder.service?.image_url && (
            <div className="flex-shrink-0 me-3">
              <img
                src={serviceOrder.service.image_url || "/placeholder.svg"}
                alt={serviceOrder.service?.title}
                className="avatar-sm rounded"
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
              />
            </div>
          )}
          <div className="flex-grow-1">
            <h6 className="mb-1">
              {truncateText(
                serviceOrder.service?.title ||
                  serviceOrder.service_name ||
                  "Service",
                25
              )}
            </h6>
            {serviceOrder.service?.category && (
              <p className="text-muted mb-0 small">
                {serviceOrder.service.category.name}
              </p>
            )}
          </div>
        </div>
      </td>

      <td className="provider">
        <div>
          <h6 className="mb-1">
            {serviceOrder.service_provider?.name || "Unknown Provider"}
          </h6>
          <p className="text-muted mb-0 small">
            {serviceOrder.service_provider?.email}
          </p>
        </div>
      </td>

      <td className="total_amount">
        <span className="fw-semibold text-success">
          {formatPrice(serviceOrder.total_amount || serviceOrder.total)}
        </span>
      </td>

      <td className="delivery_date">
        <span>
          {serviceOrder.delivery_date
            ? formatDate(serviceOrder.delivery_date)
            : "Not specified"}
        </span>
      </td>

      <td className="status">{getStatusBadge(serviceOrder.status)}</td>

      <td className="created_at">
        <span>{formatDateTime(serviceOrder.created_at)}</span>
      </td>

      <td>
        <div className="d-flex gap-2">
          <div className="show">
            <Button
              color="primary"
              size="sm"
              id={`show-${serviceOrder.id}`}
              onClick={() => handleShow(serviceOrder)}
              className="btn-icon"
            >
              <i className="ri-eye-fill"></i>
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`show-${serviceOrder.id}`}
            >
              View Details
            </UncontrolledTooltip>
          </div>

          <div className="status-update">
            <div className="dropdown">
              <Button
                color="info"
                size="sm"
                id={`status-${serviceOrder.id}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
                className="btn-icon"
              >
                <i className="ri-settings-line"></i>
              </Button>
              <UncontrolledTooltip
                placement="top"
                target={`status-${serviceOrder.id}`}
              >
                Update Status
              </UncontrolledTooltip>
              <ul className="dropdown-menu">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleStatusChange("pending")}
                    disabled={serviceOrder.status === "pending"}
                  >
                    Pending
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleStatusChange("accepted")}
                    disabled={serviceOrder.status === "accepted"}
                  >
                    Accepted
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleStatusChange("in_progress")}
                    disabled={serviceOrder.status === "in_progress"}
                  >
                    In Progress
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handleStatusChange("completed")}
                    disabled={serviceOrder.status === "completed"}
                  >
                    Completed
                  </button>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button
                    className="dropdown-item text-danger"
                    onClick={() => handleStatusChange("cancelled")}
                    disabled={serviceOrder.status === "cancelled"}
                  >
                    Cancelled
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item text-warning"
                    onClick={() => handleStatusChange("refunded")}
                    disabled={serviceOrder.status === "refunded"}
                  >
                    Refunded
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="remove">
            <Button
              color="danger"
              size="sm"
              id={`delete-${serviceOrder.id}`}
              onClick={() => handleDelete(serviceOrder.id)}
              className="btn-icon"
            >
              <i className="ri-delete-bin-fill"></i>
            </Button>
            <UncontrolledTooltip
              placement="top"
              target={`delete-${serviceOrder.id}`}
            >
              Delete Service Order
            </UncontrolledTooltip>
          </div>
        </div>
      </td>
    </tr>
  );
};

export default ServiceOrderTableItem;
